import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Eye, EyeOff, Loader2, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Mirrors the signup checklist in Auth.tsx so staff passwords are held to the
// same bar as owner passwords.
const hasMinLength = (pw: string) => pw.length >= 8;
const hasLowercase = (pw: string) => /[a-z]/.test(pw);
const hasUppercase = (pw: string) => /[A-Z]/.test(pw);
const hasNumber = (pw: string) => /[0-9]/.test(pw);
const hasSpecialChar = (pw: string) => /[^a-zA-Z0-9]/.test(pw);

type InvitePreview = {
  email: string;
  businessName: string;
  outletNames: string[];
  hasAccount: boolean;
};

/**
 * Staff invitation acceptance. The emailed token is the only credential here.
 * The page never trusts an email from the URL, it asks the edge function what
 * the token stands for.
 */
const StaffInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signInWithEmail, signOut, user } = useAuth();
  const token = searchParams.get("token");

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // One toggle drives both fields. They must match, so revealing them
  // separately only invites confusion.
  const [showPassword, setShowPassword] = useState(false);

  const checks = useMemo(
    () => ({
      minLength: hasMinLength(password),
      hasLowercase: hasLowercase(password),
      hasUppercase: hasUppercase(password),
      hasNumber: hasNumber(password),
      hasSpecial: hasSpecialChar(password),
      passwordsMatch:
        password.length > 0 && confirmPassword.length > 0 && password === confirmPassword,
    }),
    [password, confirmPassword]
  );

  const allChecksPassed =
    checks.minLength &&
    checks.hasLowercase &&
    checks.hasUppercase &&
    checks.hasNumber &&
    checks.hasSpecial &&
    checks.passwordsMatch;

  const callFunction = useCallback(
    async (body: Record<string, unknown>) => {
      const { data, error } = await supabase.functions.invoke("accept-staff-invite", { body });
      if (error) {
        return { success: false, error: "Something went wrong. Please try again." } as const;
      }
      return data as { success: boolean; error?: string } & Partial<InvitePreview>;
    },
    []
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token) {
        setLoadError("This invitation link is missing its token. Ask the business owner to send a new one.");
        setLoading(false);
        return;
      }

      const data = await callFunction({ action: "preview", token });
      if (cancelled) return;

      if (!data.success) {
        setLoadError(data.error || "This invitation link is no longer valid.");
      } else {
        setPreview({
          email: data.email!,
          businessName: data.businessName!,
          outletNames: data.outletNames || [],
          hasAccount: !!data.hasAccount,
        });
      }
      setLoading(false);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [token, callFunction]);

  const accept = async () => {
    if (!token || !preview) return;
    if (!preview.hasAccount && !allChecksPassed) {
      toast.error("Please meet all password requirements");
      return;
    }

    setSubmitting(true);
    try {
      // Accepting into a different account than the one currently signed in
      // would leave two identities fighting over the session.
      if (user && user.email?.toLowerCase() !== preview.email) {
        await signOut();
      }

      const data = await callFunction({
        action: "accept",
        token,
        password: preview.hasAccount ? undefined : password,
        // Points the confirmation email's login button back at this host.
        origin: window.location.origin,
      });

      if (!data.success) {
        toast.error(data.error || "Could not accept this invitation.");
        return;
      }

      if (preview.hasAccount) {
        toast.success("Invitation accepted. Sign in to continue.");
        navigate(`/auth?email=${encodeURIComponent(preview.email)}`);
        return;
      }

      const { error: signInError } = await signInWithEmail(preview.email, password);
      if (signInError) {
        toast.success("Account created. Please sign in to continue.");
        navigate("/auth");
        return;
      }
      toast.success(`Welcome to ${preview.businessName}!`);
      navigate("/");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError || !preview) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <ShieldAlert className="h-6 w-6 text-destructive" />
            </span>
            <CardTitle className="text-2xl font-heading font-bold">
              Invitation unavailable
            </CardTitle>
            <CardDescription>{loadError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => navigate("/auth")}
            >
              Go to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const outletSummary = preview.outletNames.length
    ? preview.outletNames.join(", ")
    : "your assigned outlets";

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-heading font-bold">
            Join {preview.businessName} on Dibs
          </CardTitle>
          <CardDescription>
            {preview.hasAccount
              ? `You already have a Dibs account for ${preview.email}. Accept to add this business to it.`
              : `Set a password for ${preview.email} to finish setting up your staff account.`}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="rounded-md border border-input bg-secondary/60 p-4">
            <p className="text-sm font-medium text-foreground">Outlet access</p>
            <p className="mt-1 text-sm text-muted-foreground">{outletSummary}</p>
          </div>

          {!preview.hasAccount && (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="staff-invite-password">Password</Label>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <Input
                  id="staff-invite-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  disabled={submitting}
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-invite-confirm">Confirm password</Label>
                <Input
                  id="staff-invite-confirm"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  disabled={submitting}
                  autoComplete="new-password"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <div className="space-y-1.5 pt-1">
                {[
                  { ok: checks.minLength, label: "At least 8 characters" },
                  { ok: checks.hasLowercase, label: "Contains a lowercase letter (a-z)" },
                  { ok: checks.hasUppercase, label: "Contains an uppercase letter (A-Z)" },
                  { ok: checks.hasNumber, label: "Contains a number (0-9)" },
                  { ok: checks.hasSpecial, label: "Contains a special character (!@#$...)" },
                  { ok: checks.passwordsMatch, label: "Passwords match" },
                ].map((req) => (
                  <div key={req.label} className="flex items-center gap-2">
                    {req.ok ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-green-500" />
                    ) : (
                      <X className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
                    )}
                    <span
                      className={`text-xs ${req.ok ? "text-green-600" : "text-muted-foreground/60"}`}
                    >
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          <Button
            className="w-full"
            disabled={submitting || (!preview.hasAccount && !allChecksPassed)}
            onClick={accept}
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {preview.hasAccount ? "Accept invitation" : "Create account and join"}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            Staff accounts can't open billing &amp; plan, connectors, or staff access.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffInvite;
